import React, { useEffect, useMemo, useState, createContext, useContext } from "react";
import { useSelector } from "react-redux";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { getThemeByMode } from "../static/styles/ThemeConfig";
import { lsGet, lsSet } from "../utils/hooks";

// Per-tab theme context — isolates theme changes from cross-tab Redux updates
export const ThemeModeContext = createContext({ mode: "default", setMode: () => {} });

export function useThemeMode() {
    return useContext(ThemeModeContext);
}

export default function AppThemeProvider({ children }) {
  const { userPermissions } = useSelector((state) => state.userPermission);

  const [mode, setMode] = useState("default");
  const [initialized, setInitialized] = useState(false);

  // Initialize ONCE when server theme first arrives — ignore subsequent Redux updates
  // This prevents other tabs' theme changes from leaking into this tab
  useEffect(() => {
    if (!initialized && userPermissions?.appliedTheme) {
      setMode(userPermissions.appliedTheme);
      setInitialized(true);
      // Sync localStorage "user" key so it reflects the server-saved theme after refresh
      const storedUser = lsGet("user");
      if (storedUser && storedUser.appliedTheme !== userPermissions.appliedTheme) {
        lsSet("user", { ...storedUser, appliedTheme: userPermissions.appliedTheme });
      }
    }
  }, [userPermissions?.appliedTheme, initialized]);

  const muiTheme = useMemo(() => getThemeByMode(mode), [mode]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
  }, [mode]);

  const contextValue = useMemo(() => ({ mode, setMode }), [mode]);

  return (
    <ThemeModeContext.Provider value={contextValue}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}
